/**
 * Storage utility for tracking task notifications to prevent duplicates
 * Uses localStorage with automatic cleanup of expired entries
 */

const STORAGE_KEY = 'benchmark-task-notifications';
const ACTIVE_NOTIFICATIONS_KEY = 'benchmark-active-task-notifications';
const CLOSED_NOTIFICATIONS_KEY = 'benchmark-closed-task-notifications';
const NOTIFICATION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface NotificationRecord {
  notifiedAt: string; // ISO timestamp
  expiresAt: string; // ISO timestamp
}

type NotificationStorage = Record<string, NotificationRecord>;
type ActiveNotificationStorage = Record<string, ActiveNotificationRecord>;
type ClosedNotificationStorage = Record<string, ClosedNotificationRecord>;

interface ActiveNotificationRecord {
  taskId: string;
  lastUpdated: string;
  manuallyClosed: boolean;
}

interface ClosedNotificationRecord {
  taskId: string;
  closedAt: string;
  expiresAt: string;
}

const getStorage = (): NotificationStorage => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to read task notification storage:', error);
    return {};
  }
};

const setStorage = (data: NotificationStorage): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to write task notification storage:', error);
  }
};

const getActiveNotificationsStorage = (): ActiveNotificationStorage => {
  try {
    const stored = localStorage.getItem(ACTIVE_NOTIFICATIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to read active task notifications storage:', error);
    return {};
  }
};

const setActiveNotificationsStorage = (data: ActiveNotificationStorage): void => {
  try {
    localStorage.setItem(ACTIVE_NOTIFICATIONS_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to write active task notifications storage:', error);
  }
};

const getClosedNotificationsStorage = (): ClosedNotificationStorage => {
  try {
    const stored = localStorage.getItem(CLOSED_NOTIFICATIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to read closed task notifications storage:', error);
    return {};
  }
};

const setClosedNotificationsStorage = (data: ClosedNotificationStorage): void => {
  try {
    localStorage.setItem(CLOSED_NOTIFICATIONS_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to write closed task notifications storage:', error);
  }
};

/**
 * Clean up expired notification records
 */
const cleanupExpired = (): void => {
  const storage = getStorage();
  const now = new Date().toISOString();
  const cleaned: NotificationStorage = {};

  let hasExpired = false;
  for (const [key, record] of Object.entries(storage)) {
    if (record.expiresAt > now) {
      cleaned[key] = record;
    } else {
      hasExpired = true;
    }
  }

  if (hasExpired) {
    setStorage(cleaned);
  }
};

/**
 * Generate a unique key for a task notification
 */
export const getNotificationKey = (taskId: string, status: string): string => {
  return `task-${taskId}-${status}`;
};

/**
 * Generate a unique key for active running task notifications
 */
export const getActiveNotificationKey = (taskId: string): string => {
  return `active-task-${taskId}`;
};

/**
 * Check if a task notification was manually closed by the user
 */
export const wasManuallyClosed = (taskId: string): boolean => {
  const storage = getClosedNotificationsStorage();
  const key = getActiveNotificationKey(taskId);
  const record = storage[key];

  if (!record) return false;

  // Check if still valid (not expired)
  const now = new Date().toISOString();
  return record.expiresAt > now;
};

/**
 * Mark a task notification as manually closed by the user
 */
export const markManuallyClosed = (taskId: string): void => {
  const storage = getClosedNotificationsStorage();
  const key = getActiveNotificationKey(taskId);
  const now = new Date();
  const closedAt = now.toISOString();
  const expiresAt = new Date(now.getTime() + NOTIFICATION_TTL_MS).toISOString();

  storage[key] = { taskId, closedAt, expiresAt };
  setClosedNotificationsStorage(storage);
};

/**
 * Check if a task has an active notification
 */
export const hasActiveNotification = (taskId: string): boolean => {
  const storage = getActiveNotificationsStorage();
  const key = getActiveNotificationKey(taskId);
  return !!storage[key];
};

/**
 * Mark a task as having an active notification
 */
export const markActiveNotification = (taskId: string, manuallyClosed = false): void => {
  const storage = getActiveNotificationsStorage();
  const key = getActiveNotificationKey(taskId);
  const now = new Date().toISOString();

  storage[key] = { taskId, lastUpdated: now, manuallyClosed };
  setActiveNotificationsStorage(storage);
};

/**
 * Remove active notification record for a task
 */
export const removeActiveNotification = (taskId: string): void => {
  const storage = getActiveNotificationsStorage();
  const key = getActiveNotificationKey(taskId);
  delete storage[key];
  setActiveNotificationsStorage(storage);
};

/**
 * Update the last updated timestamp for an active notification
 */
export const updateActiveNotification = (taskId: string): void => {
  const storage = getActiveNotificationsStorage();
  const key = getActiveNotificationKey(taskId);

  if (storage[key]) {
    storage[key].lastUpdated = new Date().toISOString();
    setActiveNotificationsStorage(storage);
  }
};

/**
 * Check if a notification has already been sent for a task's terminal status
 */
export const hasBeenNotified = (taskId: string, status: string): boolean => {
  cleanupExpired(); // Clean up before checking
  const storage = getStorage();
  const key = getNotificationKey(taskId, status);
  const record = storage[key];

  if (!record) return false;

  // Check if still valid (not expired)
  const now = new Date().toISOString();
  return record.expiresAt > now;
};

/**
 * Mark a notification as sent for a task's terminal status
 */
export const markNotified = (taskId: string, status: string): void => {
  const storage = getStorage();
  const key = getNotificationKey(taskId, status);
  const now = new Date();
  const notifiedAt = now.toISOString();
  const expiresAt = new Date(now.getTime() + NOTIFICATION_TTL_MS).toISOString();

  storage[key] = { notifiedAt, expiresAt };
  setStorage(storage);
};

/**
 * Clear all notification records (useful for testing or manual reset)
 */
export const clearAllNotifications = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear task notification storage:', error);
  }
};
